/**
 * InspectionChecklist - Renders the snapshotted checklist items for
 * an inspection. Mirrors the per-row layout of WorkflowChecklist
 * (so the field-side feel is consistent for both flows) but swaps
 * the binary checkbox for three rating buttons (utils/inspectionRating):
 *
 *   - Good
 *   - Needs Attention  (a note is optional)
 *   - Critical         (requires a note)
 *
 * When the user picks Needs Attention or Critical a 1-row auto-grow
 * textarea appears beneath the rating buttons; for Critical, saving is
 * gated on a non-empty note (matching the server-side validation rule). Re-tapping the
 * currently-selected rating clears it (returns the row to un-rated).
 *
 * At the bottom of the list two free-form fields render below the
 * items, on the inspection record itself rather than per-item:
 *
 *   - "Recommendations / Remarks" — 3-row auto-grow textarea
 *   - "Follow-Up (if any)" — 1-row auto-grow textarea
 *
 * All inputs (ratings, notes, bottom textareas) autosave: rating
 * clicks fire onRateItem immediately; textareas debounce-on-blur
 * so the parent can PATCH the inspection without a save button.
 *
 * Photo capture + thumbnails reuse the same wiring as the WO
 * workflow (onCapturePhotoForItem, capturedPhotosByItem, onExpandPhoto).
 *
 * @see WorkflowChecklist for the WO equivalent.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import type { ChecklistItem, CapturedPhotoPhases } from '../../../services/checklistService';
import type { ChecklistItemRating } from '../../../services/checklistInstanceService';
import { AutoGrowTextarea, PhotoThumbnail } from '../../atoms';
import { ChecklistItemPhotoActions } from '../../molecules/ChecklistItemPhotoActions';
import type { CapturePhase } from '../../molecules/ChecklistItemPhotoActions';
import { formatAttribution } from '../../../utils/formatDate';
import { NoteBlurBridge } from '../../molecules/NoteBlurBridge';
import { RatingPicker } from '../../molecules/RatingPicker';
import type { InspectionRating } from '../../../utils/inspectionRating';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * One of the four rating values, with display metadata. Order in
 * this array IS the render order on screen.
 */
export interface InspectionChecklistProps {
	/** Snapshotted instance items to render. */
	items: ChecklistItem[];
	/**
	 * Persist a rating change for an item. The handler is responsible
	 * for the API call; the component optimistically updates locally
	 * via `items` re-render once the parent state changes. Pass null
	 * for `rating` to clear the rating.
	 */
	onRateItem: (
		itemId: string,
		rating: ChecklistItemRating | null,
		ratingNote: string | null,
	) => Promise<void>;
	/** Current Recommendations / Remarks textarea value (controlled). */
	recommendationsRemarks: string;
	/**
	 * Persist Recommendations / Remarks. Called on blur after a change
	 * (parent owns the debounce / request). Receives the trimmed value.
	 */
	onSaveRecommendationsRemarks: (value: string) => Promise<void>;
	/** Current Follow-Up textarea value (controlled). */
	followUpNotes: string;
	/** Persist Follow-Up notes — same blur-save contract. */
	onSaveFollowUpNotes: (value: string) => Promise<void>;
	/**
	 * When true the entire surface is read-only (no rating taps,
	 * no textarea edits). Used outside the in_progress inspection
	 * window or for users without edit access.
	 */
	disabled?: boolean;
	/** Optional offline indicator strip. */
	isOffline?: boolean;
	/** Optional error banner text. */
	error?: string | null;
	/**
	 * Optional camera CTA handler. Phase is undefined for single-photo
	 * items and 'before' / 'after' for before_after items so the
	 * parent's modal can scope the upload accordingly. Mirrors the WO
	 * workflow's contract.
	 */
	onCapturePhotoForItem?: (itemId: string, phase?: CapturePhase) => void;
	/** Per-item local flags for "captured this session". Drives the
	 *  "Done" badge before the parent refetch lands. */
	capturedPhotosByItem?: Map<string, CapturedPhotoPhases>;
	/** Expand-thumbnail handler — opens the parent's PhotoLightbox. */
	onExpandPhoto?: (photoUrl: string, meta: { caption: string | null; area: string | null }) => void;
	/** Optional extra classes for the outer wrapper */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

const InspectionChecklist: React.FC<InspectionChecklistProps> = ({
	items,
	onRateItem,
	recommendationsRemarks,
	onSaveRecommendationsRemarks,
	followUpNotes,
	onSaveFollowUpNotes,
	disabled = false,
	isOffline = false,
	error,
	onCapturePhotoForItem,
	capturedPhotosByItem,
	onExpandPhoto,
	className,
}) => {
	/* Sort by sortOrder once per render — items are small. */
	const sortedItems = [...items].sort((a, b) => a.sortOrder - b.sortOrder);

	/* Progress: how many items have any rating set. The "rating"
	 * field comes through the same ChecklistItem shape — read it
	 * defensively because the WO type alias doesn't have it
	 * declared yet. */
	const ratedCount = sortedItems.filter((it) => !!(it as any).rating).length;
	const totalCount = sortedItems.length;
	const progressPercent = totalCount > 0 ? Math.round((ratedCount / totalCount) * 100) : 0;

	/* Required-item progress mirrors WorkflowChecklist for parity —
	 * counts items flagged isRequired that have NOT been rated yet. */
	const requiredItems = sortedItems.filter((it) => it.isRequired);
	const requiredRemaining = requiredItems.filter((it) => !(it as any).rating).length;

	return (
		<div className={`inspection-checklist flex flex-col ${className ?? ''}`} data-ref="organism-inspection-checklist">
			{/* --- Progress header --- */}
			<div className="inspection-checklist__progress px-16px pt-16px pb-12px">
				<div className="flex items-center justify-between mb-8px">
					<span className="text-14px font-sans font-semibold text-brand-text">
						{ratedCount} of {totalCount} rated
					</span>
					<span className="text-14px font-sans font-medium text-brand-text-muted">
						{progressPercent}%
					</span>
				</div>
				<div className="w-full h-8px bg-gray-200 rounded-full overflow-hidden">
					<div className="h-full bg-brand-primary rounded-full transition-all duration-300 ease-out" style={{ width: `${progressPercent}%` }} />
				</div>

				{requiredItems.length > 0 && requiredRemaining > 0 && (
					<p className="text-12px font-sans text-brand-text-muted mt-4px">
						{requiredRemaining} required item{requiredRemaining === 1 ? '' : 's'} remaining
					</p>
				)}

				{isOffline && (
					<div className="flex items-center gap-6px mt-8px px-10px py-6px bg-amber-50 border border-amber-200 rounded-md">
						<svg className="h-14px w-14px text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
							<path strokeLinecap="round" strokeLinejoin="round" d="M12 9.75v6.75m0 0-3-3m3 3 3-3m-8.25 6a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
						</svg>
						<span className="text-12px font-sans text-amber-700">Offline — changes will sync when you reconnect</span>
					</div>
				)}

				{error && (
					<div className="flex items-center gap-6px mt-8px px-10px py-6px bg-red-50 border border-red-200 rounded-md">
						<svg className="h-14px w-14px text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
							<path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
						</svg>
						<span className="text-12px font-sans text-red-700">{error}</span>
					</div>
				)}
			</div>

			{/* --- Item list --- */}
			<div className="px-16px pb-16px">
				<ul className="inspection-checklist__list flex flex-col gap-6px">
					{sortedItems.map((item) => (
						<InspectionChecklistRow
							key={item.id}
							item={item}
							onRateItem={onRateItem}
							disabled={disabled}
							onCapturePhotoForItem={onCapturePhotoForItem}
							capturedPhotosByItem={capturedPhotosByItem}
							onExpandPhoto={onExpandPhoto}
						/>
					))}
				</ul>

				{sortedItems.length === 0 && (
					<p className="text-center text-14px font-sans text-brand-text-muted py-32px">
						No checklist items have been assigned to this inspection.
					</p>
				)}
			</div>

			{/* --- Bottom textareas --- */}
			{/* These two fields live on the inspection record itself
			 * (recommendations_remarks + follow_up_notes columns) so
			 * they render below the per-item ratings and persist on
			 * blur via PATCH /inspections/{id}. */}
			<div className="px-16px pb-24px space-y-16px">
				<BlurAutoSaveField
					label="Recommendations / Remarks"
					value={recommendationsRemarks}
					onSave={onSaveRecommendationsRemarks}
					minRows={3}
					placeholder="Overall recommendations, summary remarks..."
					disabled={disabled}
				/>
				<BlurAutoSaveField
					label="Follow-Up (if any)"
					value={followUpNotes}
					onSave={onSaveFollowUpNotes}
					minRows={1}
					placeholder="Anything that needs follow-up..."
					disabled={disabled}
				/>
			</div>
		</div>
	);
};

/* ============================================
 * SUBCOMPONENTS
 * ============================================ */

interface InspectionChecklistRowProps {
	item: ChecklistItem;
	onRateItem: InspectionChecklistProps['onRateItem'];
	disabled: boolean;
	onCapturePhotoForItem?: (itemId: string, phase?: CapturePhase) => void;
	capturedPhotosByItem?: Map<string, CapturedPhotoPhases>;
	onExpandPhoto?: (photoUrl: string, meta: { caption: string | null; area: string | null }) => void;
}

/**
 * Single row in the inspection list. Pulled out so the local note
 * draft state lives per-row instead of in a parent map (cleaner
 * unmount semantics; no stale rows in a sibling's draft buffer).
 */
const InspectionChecklistRow: React.FC<InspectionChecklistRowProps> = ({
	item,
	onRateItem,
	disabled,
	onCapturePhotoForItem,
	capturedPhotosByItem,
	onExpandPhoto,
}) => {
	/* Rating + note come through the same ChecklistItem shape that
	 * the WO checklist uses, but the rating fields are inspection-only
	 * additions on the server — read them defensively from the
	 * unwrapped object so a stale TS shape doesn't fail the build. */
	const rating = ((item as any).rating ?? null) as ChecklistItemRating | null;
	/* The reason for the current tier (rating_note; the adapter folds the
	   retired dissatisfied_note into it for historical rows). */
	const persistedNote = item.ratingNote ?? '';

	/* Rating interaction — tier buttons, the guidance line, and the reason
	   field — lives in the shared RatingPicker molecule so this surface and the
	   field workflow behave identically. The row only has to say what happens
	   when a rating is chosen. */
	const handleRate = useCallback(
		async (next: InspectionRating | null, note: string | null) => {
			await onRateItem(item.id, next, note);
		},
		[item.id, onRateItem],
	);

	const completedByName = item.completedByName ?? null;
	const completedAt = item.completedAt ?? null;
	const showAttribution = !!rating && !!completedByName;

	return (
		<li className="inspection-checklist__item">
			<div className={['w-full flex flex-col rounded-lg border transition-all duration-150 overflow-hidden', rating ? 'bg-brand-surface/50 border-brand-border' : 'bg-white border-gray-200'].join(' ')}>
				{/* Top row: title + photo CTA */}
				<div className="flex items-stretch gap-8px">
					<div className="flex-1 p-12px">
						<div className="flex items-start justify-between gap-8px">
							<span className="text-15px font-sans font-medium leading-snug text-brand-text">
								{item.title}
							</span>
							<div className="flex-shrink-0 flex items-center gap-4px">
								{item.isRequired && (
									<span className="text-10px font-sans font-medium text-amber-700 bg-amber-100 px-6px py-2px rounded uppercase tracking-wide">
										Required
									</span>
								)}
							</div>
						</div>

						{item.details && (
							<p className="text-13px font-sans leading-snug mt-4px whitespace-pre-line text-brand-text-muted">
								{item.details}
							</p>
						)}

						{item.additionalDetails && (
							<p className="text-12px font-sans leading-snug mt-4px italic px-8px py-4px rounded bg-amber-50 text-amber-800 border border-amber-100">
								{item.additionalDetails}
							</p>
						)}

						{/* Three-tier rating — Good / Needs Attention / Critical */}
						<RatingPicker
							className="mt-10px"
							rating={rating}
							note={persistedNote}
							onRate={handleRate}
							disabled={disabled}
						/>

					{/* Attribution — who rated, when. Mirrors the WO
						 *  workflow's "completed by X" line so users see
						 *  the same metadata across both surfaces. */}
						{showAttribution && (
							<span className="text-11px font-sans text-brand-text-muted mt-8px block">
								{completedByName} &middot;{' '}
								{completedAt ? formatAttribution(completedAt) : 'just now'}
							</span>
						)}
					</div>

					{/* Camera CTA — shared molecule, see WorkflowChecklist. */}
					{onCapturePhotoForItem && (
						<ChecklistItemPhotoActions
							item={item}
							onCapture={onCapturePhotoForItem}
							captured={capturedPhotosByItem?.get(item.id)}
							/* Every inspection item gets a camera, not only the
							   ones the template asked for a photo on. */
							optionalPhoto
						/>
					)}
				</div>

				{/* Captured photos strip — BEFORE/AFTER overlay tag on
				 *  thumbnails when the item is in before_after mode. */}
				{item.photos && item.photos.length > 0 && (
					<div className="flex flex-wrap gap-6px px-12px py-8px border-t border-gray-100 bg-gray-50/50">
						{item.photos.map((photo) => (
							<div key={photo.id} className="relative">
								<PhotoThumbnail src={photo.url} alt={photo.caption ?? 'Captured photo'} size="md" onExpand={onExpandPhoto ? () => onExpandPhoto(photo.url, { caption: photo.caption, area: photo.area }) : undefined} />
								{item.photoMode === 'before_after' && photo.photoPhase && (
									<span className="absolute top-2px left-2px text-9px font-bold uppercase tracking-wide bg-black/70 text-white px-4px py-1px rounded">
										{photo.photoPhase}
									</span>
								)}
							</div>
						))}
					</div>
				)}
			</div>
		</li>
	);
};

/* ============================================
 * SMALL UTILITY COMPONENTS
 * ============================================ */

/**
 * Auto-saving textarea field for the bottom-of-form "Recommendations"
 * and "Follow-Up" inputs. Keeps a local draft, persists on blur if
 * the value changed since the last save, and stays in sync if the
 * parent value updates externally (e.g. after a refetch).
 */
interface BlurAutoSaveFieldProps {
	label: string;
	value: string;
	onSave: (next: string) => Promise<void>;
	minRows: number;
	placeholder?: string;
	disabled?: boolean;
}

const BlurAutoSaveField: React.FC<BlurAutoSaveFieldProps> = ({ label, value, onSave, minRows, placeholder, disabled = false }) => {
	const [draft, setDraft] = useState<string>(value);
	const lastSavedRef = useRef<string>(value);
	useEffect(() => {
		setDraft(value);
		lastSavedRef.current = value;
	}, [value]);

	const handleBlur = async () => {
		if (disabled) return;
		const trimmed = draft;
		if (trimmed === lastSavedRef.current) return;
		lastSavedRef.current = trimmed;
		await onSave(trimmed);
	};

	return (
		<div>
			<label className="block text-12px font-sans font-medium text-brand-text mb-4px">
				{label}
			</label>
			<AutoGrowTextarea value={draft} onChange={setDraft} placeholder={placeholder} minRows={minRows} disabled={disabled} />
			<NoteBlurBridge onBlur={handleBlur} />
		</div>
	);
};

export default InspectionChecklist;
